/**
 * Months on the Photos feed (#665): the month headings in the timeline and
 * the "jump to month" picker.
 *
 * Everything here works on the SITE DAY string (`YYYY-MM-DD`) the API sends,
 * and builds dates from local components only — never `toISOString()`, whose
 * UTC date runs a day ahead every evening in Eastern and has put things in the
 * wrong month before (see modules/qaqc/pourRange.ts). A site day has no time
 * of day, so string arithmetic is enough and there is nothing to convert.
 */

const MONTH = /^(\d{4})-(0[1-9]|1[0-2])$/;
const YMD = /^(\d{4})-(\d{2})-(\d{2})$/;

/** The `YYYY-MM` a site day falls in, or '' for anything that isn't one. */
export function monthOf(ymd: string): string {
  const m = YMD.exec(ymd);
  return m ? `${m[1]}-${m[2]}` : '';
}

/** The first and last site day of a `YYYY-MM` month, inclusive, or null. */
export function monthRange(month: string): { from: string; to: string } | null {
  const m = MONTH.exec(month);
  if (!m) return null;
  const year = Number(m[1]);
  const mon = Number(m[2]);
  // Day 0 of the NEXT month is the last day of this one, in local time.
  const last = new Date(year, mon, 0).getDate();
  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` };
}

/**
 * The month a From/To pair covers exactly, or '' when it's empty or any other
 * range. Lets the picker show the month a reader jumped to, and step aside
 * when they've typed their own dates.
 */
export function monthOfRange(from: string, to: string): string {
  const month = monthOf(from);
  const r = month ? monthRange(month) : null;
  return r && r.from === from && r.to === to ? month : '';
}

/** "October 2026" for `2026-10`; the input itself if it isn't a month. */
export function monthLabel(month: string): string {
  const m = MONTH.exec(month);
  if (!m) return month;
  return new Date(Number(m[1]), Number(m[2]) - 1, 1)
    .toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}
